(() => {
  if (window.__optimized_app_loader_initialized) return;
  window.__optimized_app_loader_initialized = true;

  // list of known third‑party app script URLs; referenced in tests and used for
  // building the fallback list. The MutationObserver blocks scripts via
  // `shouldBlockScript()` and the broader `blockedScriptPatterns`; the `apps`
  // array defines the concrete URLs consumed by the fallback loader and the
  // unit tests.
  const apps = [
    "https://d3hw6dc1ow8pp2.cloudfront.net/reviews-widget-plus/js/okendo-reviews.js",
    "https://static.klaviyo.com/onsite/js/XsEe4C/klaviyo.js?company_id=XsEe4C",
    "https://static.klaviyo.com/onsite/js/sharedUtils.b7d7d70055e8927a6d15.js?cb=2",
    // newer URLs added to tests; keep in sync so the loader actually blocks and loads them
    "https://assets.gorgias.chat/widget-loader.js",
    "https://cdn.shopify.com/extensions/019c1c9b-69a5-75c7-99f1-f2ea136270a3/pagefly-page-builder-218/assets/pagefly-htmlvideo2.js"
  ];

  // patterns used to identify third‑party scripts that should be delayed. This is
  // intentionally broader than the hard‑coded `apps` list so we catch generic
  // URLs (e.g. any `static.klaviyo.com/onsite/js/` script).
  const blockedScriptPatterns = [
    "d3hw6dc1ow8pp2.cloudfront.net/reviews-widget-plus/js/okendo-reviews.js",
    "static.klaviyo.com/onsite/js/",
    "assets.gorgias.chat/",
    "pagefly-page-builder",
    "www.fitnessavenue.ca/wpm/",
    "web-pixel-",
    "googletagmanager.com/gtag/js",
    "connect.facebook.net/en_US/fbevents.js",
    "scripts.clarity.ms/",
    "sapp.multivariants.com/js/init.js",
    "assets.cloudlift.app/assets/options.js",
    "media.9gtb.com/script.js",
    "s1.staq-cdn.com/"
  ];

  const shouldBlockScript = (src) => {
    if (!src) return false;
    return blockedScriptPatterns.some((pattern) => src.includes(pattern));
  };

  const blockedStylesheetPatterns = [
    "fonts.bunny.net/css",
    "use.typekit.net/",
    "p.typekit.net/"
  ];

  const lowValuePreconnectHosts = [
    "shop.app",
    "api.config-security.com",
    "conf.config-security.com"
  ];

  const normalizeRel = (rel = "") => rel.toLowerCase().split(/\s+/).filter(Boolean);

  const shouldBlockLink = (link) => {
    const href = link.getAttribute("href") || "";
    if (!href) return false;
    const relTokens = normalizeRel(link.getAttribute("rel") || "");

    const isStylesheet = relTokens.includes("stylesheet");
    if (isStylesheet) {
      return blockedStylesheetPatterns.some((pattern) => href.includes(pattern));
    }

    const isConnectionHint = relTokens.includes("preconnect") || relTokens.includes("dns-prefetch");
    if (!isConnectionHint) return false;

    return lowValuePreconnectHosts.some((host) => href.includes(host));
  };

  const shouldRestoreLink = (link) => {
    const relTokens = normalizeRel(link.getAttribute("rel") || "");
    return relTokens.includes("stylesheet");
  };

  // store clones of the original <script> nodes to preserve attributes on restore
  const blockedScripts = [];
  const blockedHeadResources = [];

  // fallback list used when nothing was blocked before interaction
  const defaultBlockedScripts = [
    {src: apps[0], id: "okendo-reviews-script"},
    {src: apps[1], crossOrigin: "anonymous"},
    {src: apps[2], crossOrigin: "anonymous"},
    {src: apps[3]},
    {src: apps[4]}
  ];

  window.__apps_unblocked = false;

  const getBodyTarget = () => document.body;
  const getHideTarget = () => document.body || document.documentElement;

  const initInteractionHiding = () => {
    const hideTarget = getHideTarget();
    const hiddenTarget = hideTarget === document.documentElement ? "html" : "body";
    if (hideTarget) {
      hideTarget.classList.add("itg_before_hide");
    }
    const events = ["mousemove", "click", "touchstart", "pointerdown", "scroll", "touchmove", "wheel"];
    const MAX_HIDE_MS = 1200;

    const removeClass = () => {
      const htmlEl = document.documentElement;
      const bodyEl = document.body;
      if (htmlEl) {
        htmlEl.classList.remove("itg_before_hide");
      }
      if (bodyEl) {
        bodyEl.classList.remove("itg_before_hide");
      }
      if (hiddenTarget === "html" && htmlEl) {
        htmlEl.classList.remove("itg_before_hide");
      }
      if (hiddenTarget === "body" && bodyEl) {
        bodyEl.classList.remove("itg_before_hide");
      }
      events.forEach((eventName) => {
        window.removeEventListener(eventName, removeClass, { passive: true, capture: true });
        document.removeEventListener(eventName, removeClass, { passive: true, capture: true });
      });
      document.removeEventListener("DOMContentLoaded", removeClass);
      window.removeEventListener("load", removeClass);
      if (window.__interaction_hide_timeout) {
        window.clearTimeout(window.__interaction_hide_timeout);
        window.__interaction_hide_timeout = null;
      }
    };

    events.forEach((eventName) => {
      window.addEventListener(eventName, removeClass, { passive: true, capture: true });
      document.addEventListener(eventName, removeClass, { passive: true, capture: true });
    });

    document.addEventListener("DOMContentLoaded", removeClass, { once: true });
    window.addEventListener("load", removeClass, { once: true });
    window.__interaction_hide_timeout = window.setTimeout(removeClass, MAX_HIDE_MS);
  };

  const blockScriptObserver = new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node.tagName === "SCRIPT" && shouldBlockScript(node.src)) {
          // capture a deep clone of the element so attributes aren't lost
          blockedScripts.push(node.cloneNode(true));
          node.remove();
        } else if (node.tagName === "LINK" && shouldBlockLink(node)) {
          blockedHeadResources.push(node.cloneNode(true));
          node.remove();
        }
      });
    });
  });

  let blockedScriptsLoaded = false;
  const loadBlockedScripts = () => {
    if (blockedScriptsLoaded) return;
    blockedScriptsLoaded = true;

    // choose the list of scripts to restore: captured clones or fallback defaults
    const scriptsToRestore = blockedScripts.length
      ? blockedScripts.map((script) => {
          // clones preserve any attributes the original element had, but tests
          // and some vendors rely on us adding a few defaults when attributes are
          // missing.  Merge in whatever we spilled in defaultBlockedScripts.
          const match = defaultBlockedScripts.find((info) => script.src.includes(info.src));
          if (match) {
            if (match.id && !script.id) script.id = match.id;
            if (match.crossOrigin && !script.crossOrigin) script.crossOrigin = match.crossOrigin;
          }

          // make sure async is set (needed by some vendors after cloning)
          script.async = true;
          return script;
        })
      : defaultBlockedScripts.map((info) => {
          const el = document.createElement("script");
          el.src = info.src;
          if (info.id) el.id = info.id;
          if (info.crossOrigin) el.crossOrigin = info.crossOrigin;
          return el;
        });

    const appendTarget = getBodyTarget();
    if (appendTarget) {
      scriptsToRestore.forEach((script) => {
        appendTarget.appendChild(script);
      });
    } else {
      document.addEventListener("DOMContentLoaded", () => {
        const readyTarget = getBodyTarget();
        if (!readyTarget) return;
        scriptsToRestore.forEach((script) => {
          readyTarget.appendChild(script);
        });
      }, { once: true });
    }

    blockedHeadResources.forEach((resource) => {
      if (!shouldRestoreLink(resource)) return;
      const href = resource.getAttribute("href");
      const rel = resource.getAttribute("rel") || "";
      if (!href) return;
      const hasDuplicate = Array.prototype.some.call(
        document.head.querySelectorAll("link"),
        (link) =>
          link.getAttribute("href") === href &&
          (link.getAttribute("rel") || "") === rel
      );
      if (hasDuplicate) return;
      document.head.appendChild(resource);
    });

    window.__apps_unblocked = true;
  };

  const captureAndRemoveExistingHeadResources = () => {
    document.head.querySelectorAll("link[href]").forEach((link) => {
      if (shouldBlockLink(link)) {
        blockedHeadResources.push(link.cloneNode(true));
        link.remove();
      }
    });
  };

  captureAndRemoveExistingHeadResources();

  blockScriptObserver.observe(document.documentElement, { childList: true, subtree: true });

  const loadBlockedApps = () => {
    blockScriptObserver.disconnect();
    loadBlockedScripts();
  };

  const attachLoadTriggers = () => {
    window.addEventListener("scroll", loadBlockedApps, { passive: true, once: true });
    document.addEventListener("pointerdown", loadBlockedApps, { passive: true, once: true });
    document.addEventListener("keydown", loadBlockedApps, { once: true });
    window.setTimeout(loadBlockedApps, 120000);
  };

  initInteractionHiding();

  if (document.readyState === "complete") {
    attachLoadTriggers();
  } else {
    window.addEventListener("load", attachLoadTriggers, { once: true });
  }
})();
